// src/modules/dl/CNNLab.jsx
// CNN Lab — fully visual convolutional network simulator
// Sliding kernel · Feature maps · Pooling · Multi-filter · Receptive field · Architecture builder

import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--blue)';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(+e.target.value)}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

// ─── MATH ────────────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

const conv2D = (img, kernel, stride=1, pad=0) => {
  const H=img.length, W=img[0].length, KH=kernel.length, KW=kernel[0].length;
  const pH = pad>0 ? Array.from({length:H+2*pad},(_,i)=>Array.from({length:W+2*pad},(_,j)=>{const pi=i-pad,pj=j-pad;return(pi>=0&&pi<H&&pj>=0&&pj<W)?img[pi][pj]:0;})) : img;
  const oH=Math.floor((H+2*pad-KH)/stride)+1, oW=Math.floor((W+2*pad-KW)/stride)+1;
  return Array.from({length:oH},(_,i)=>Array.from({length:oW},(_,j)=>kernel.reduce((s,kr,ki)=>s+kr.reduce((ss,kv,kj)=>ss+kv*pH[i*stride+ki][j*stride+kj],0),0)));
};

const maxPool = (fm, size=2, stride=2) => {
  const H=fm.length, W=fm[0].length;
  const oH=Math.floor((H-size)/stride)+1, oW=Math.floor((W-size)/stride)+1;
  return Array.from({length:oH},(_,i)=>Array.from({length:oW},(_,j)=>{
    let mx=-Infinity;
    for(let di=0;di<size;di++) for(let dj=0;dj<size;dj++) if(fm[i*stride+di]?.[j*stride+dj]!==undefined) mx=Math.max(mx,fm[i*stride+di][j*stride+dj]);
    return mx;
  }));
};

const avgPool = (fm, size=2, stride=2) => {
  const H=fm.length, W=fm[0].length;
  const oH=Math.floor((H-size)/stride)+1, oW=Math.floor((W-size)/stride)+1;
  return Array.from({length:oH},(_,i)=>Array.from({length:oW},(_,j)=>{
    let s=0,n=0;
    for(let di=0;di<size;di++) for(let dj=0;dj<size;dj++) if(fm[i*stride+di]?.[j*stride+dj]!==undefined){s+=fm[i*stride+di][j*stride+dj];n++;}
    return n?s/n:0;
  }));
};

const applyReLU = m => m.map(r=>r.map(v=>Math.max(0,v)));

const KERNELS = {
  edge_detect:  [[-1,-1,-1],[-1,8,-1],[-1,-1,-1]],
  sobel_x:      [[-1,0,1],[-2,0,2],[-1,0,1]],
  sobel_y:      [[-1,-2,-1],[0,0,0],[1,2,1]],
  sharpen:      [[0,-1,0],[-1,5,-1],[0,-1,0]],
  blur:         [[1/9,1/9,1/9],[1/9,1/9,1/9],[1/9,1/9,1/9]],
  emboss:       [[-2,-1,0],[-1,1,1],[0,1,2]],
  vert_lines:   [[-1,2,-1],[-1,2,-1],[-1,2,-1]],
  horiz_lines:  [[-1,-1,-1],[2,2,2],[-1,-1,-1]],
};

const genImage = (type='checkerboard', size=16) => {
  if(type==='checkerboard') return Array.from({length:size},(_,i)=>Array.from({length:size},(_,j)=>{
    const c=((Math.floor(i/3)+Math.floor(j/3))%2)*0.7;
    return Math.min(1,Math.max(0,c+(i+j)/(2*size)*0.3+randn()*0.05));
  }));
  if(type==='gradient') return Array.from({length:size},(_,i)=>Array.from({length:size},(_,j)=>i/size));
  if(type==='circle') return Array.from({length:size},(_,i)=>Array.from({length:size},(_,j)=>{
    const dx=i-size/2, dy=j-size/2, r=Math.sqrt(dx*dx+dy*dy);
    return r<size/3?1:r<size/2?0.5:0;
  }));
  if(type==='stripes') return Array.from({length:size},(_,i)=>Array.from({length:size},(_,j)=>((i+j)%4<2)?0.9:0.1));
  if(type==='random') return Array.from({length:size},()=>Array.from({length:size},()=>Math.random()));
  return genImage('checkerboard', size);
};

const TABS = ['kernel-viz','feature-maps','pooling','multi-filter','architecture'];

// ─── PIXEL GRID ──────────────────────────────────────────────────────────────
function PixelGrid({ matrix, title, color='#60a5fa', cellSize=14, highlight=null, highlightK=null, kernelSize=3, accent=false }) {
  if(!matrix?.length) return null;
  const flat=matrix.flat(), mn=Math.min(...flat), mx=Math.max(...flat), rng=mx-mn||1;
  return (
    <div>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.08em'}}>{title} ({matrix.length}×{matrix[0].length})</div>
      <div style={{display:'inline-flex',flexDirection:'column',gap:'1px',border:accent?`2px solid ${color}`:'1px solid #1e293b',borderRadius:'4px',overflow:'hidden',boxShadow:accent?`0 0 12px ${color}44`:'none'}}>
        {matrix.map((row,i)=>(
          <div key={i} style={{display:'flex',gap:'1px'}}>
            {row.map((v,j)=>{
              const norm=(v-mn)/rng;
              const isHL = highlight && i>=highlight[0] && i<highlight[0]+kernelSize && j>=highlight[1] && j<highlight[1]+kernelSize;
              const bg = color==='#f87171'
                ? v>=0 ? `rgba(74,222,128,${0.08+norm*0.92})` : `rgba(248,113,113,${0.08+Math.abs(v/mx)*0.92})`
                : `${color}${Math.round((0.06+norm*0.94)*255).toString(16).padStart(2,'0')}`;
              return (
                <div key={j} title={v.toFixed(3)} style={{
                  width:cellSize,height:cellSize,background:bg,
                  border:isHL?`2px solid #fbbf24`:'none',boxSizing:'border-box',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:`${Math.max(5,cellSize*0.38)}px`,color:norm>0.6?'#000':'#e2e8f0',
                  fontFamily:'JetBrains Mono',flexShrink:0,
                }}>
                  {cellSize>=14?v.toFixed(cellSize>=18?1:0):''}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── KERNEL DISPLAY ──────────────────────────────────────────────────────────
function KernelDisplay({ kernel, label }) {
  if(!kernel?.length) return null;
  const flat=kernel.flat(), mx=Math.max(0.01,...flat.map(Math.abs));
  return (
    <div>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.08em'}}>{label}</div>
      <div style={{display:'inline-grid',gridTemplateColumns:`repeat(${kernel[0].length},1fr)`,gap:'2px',border:'1px solid #1e293b',borderRadius:'3px',padding:'3px',background:'#060b14'}}>
        {flat.map((v,i)=>{
          const t=v/mx;
          const bg=t>0?`rgba(96,165,250,${0.1+t*0.9})`:`rgba(248,113,113,${0.1+(-t)*0.9})`;
          return <div key={i} style={{width:36,height:36,background:bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',color:Math.abs(t)>0.5?'#000':'#e2e8f0',fontFamily:'JetBrains Mono',fontWeight:700,borderRadius:'2px'}}>{v%1===0?v:v.toFixed(2)}</div>;
        })}
      </div>
    </div>
  );
}

export default function CNNLab() {
  const [tab,setTab]=useState('kernel-viz');
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'CNN Lab — pick a kernel, watch it slide across the image.',t:'info'}]);
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  // KERNEL VIZ tab
  const [kernelName,setKernelName]=useState('edge_detect');
  const [imgType,setImgType]=useState('checkerboard');
  const [stride,setStride]=useState(1);
  const [padding,setPadding]=useState(0);
  const [imgSize,setImgSize]=useState(14);
  const [hlPos,setHlPos]=useState([0,0]);
  const [animating,setAnimating]=useState(false);
  const [convResult,setConvResult]=useState(null);
  const [image,setImage]=useState(()=>genImage('checkerboard',14));
  const animRef=useRef(null);

  // FEATURE MAPS tab
  const [fmImage,setFmImage]=useState(()=>genImage('checkerboard',16));
  const [fmResult,setFmResult]=useState(null);

  // POOLING tab
  const [poolType,setPoolType]=useState('max');
  const [poolSize,setPoolSize]=useState(2);
  const [poolResult,setPoolResult]=useState(null);
  const [poolImage,setPoolImage]=useState(()=>genImage('checkerboard',12));

  // MULTI FILTER tab
  const [mfResult,setMfResult]=useState(null);

  // ARCHITECTURE tab
  const [archLayers,setArchLayers]=useState([
    {type:'conv',filters:8,kernel:3,stride:1,pad:0},
    {type:'relu'},
    {type:'maxpool',size:2,stride:2},
    {type:'conv',filters:16,kernel:3,stride:1,pad:1},
    {type:'relu'},
    {type:'maxpool',size:2,stride:2},
    {type:'flatten'},
    {type:'fc',neurons:64},
    {type:'fc',neurons:10},
  ]);

  // Rebuild image when type/size changes
  useEffect(()=>{ setImage(genImage(imgType,imgSize)); setConvResult(null); },[imgType,imgSize]);

  const runConv = (img=image, k=KERNELS[kernelName], s=stride, p=padding) => {
    const out = conv2D(img,k,s,p);
    const relu = applyReLU(out);
    setConvResult({raw:out,relu});
    log(`Conv ${k.length}×${k.length} "${kernelName}" | stride=${s} pad=${p} | ${img.length}×${img[0].length} → ${out.length}×${out[0].length}`,'ok');
    return out;
  };

  // Animate kernel sliding
  const animateConv = () => {
    if(animating){clearInterval(animRef.current);setAnimating(false);return;}
    runConv();
    setAnimating(true);
    const k=KERNELS[kernelName];
    const out=conv2D(image,k,stride,padding);
    setConvResult({raw:out,relu:applyReLU(out)});
    const maxI=image.length-k.length, maxJ=image[0].length-k[0].length;
    let pos=[0,0];
    animRef.current=setInterval(()=>{
      setHlPos([pos[0],pos[1]]);
      pos[1]+=stride;
      if(pos[1]>maxJ){pos[1]=0;pos[0]+=stride;}
      if(pos[0]>maxI){clearInterval(animRef.current);setAnimating(false);setHlPos([0,0]);}
    },120);
  };

  const runFeatureMaps = () => {
    const results={};
    Object.entries(KERNELS).forEach(([name,k])=>{
      const raw=conv2D(fmImage,k,1,0);
      results[name]={raw,relu:applyReLU(raw)};
    });
    setFmResult(results);
    log(`Feature maps: ${Object.keys(KERNELS).length} filters on ${fmImage.length}×${fmImage[0].length} image`,'ok');
  };

  const runPooling = () => {
    const raw=conv2D(poolImage,KERNELS.edge_detect,1,0);
    const relu=applyReLU(raw);
    const pooled = poolType==='max' ? maxPool(relu,poolSize,poolSize) : avgPool(relu,poolSize,poolSize);
    setPoolResult({raw,relu,pooled});
    log(`${poolType.toUpperCase()}POOL ${poolSize}×${poolSize} | ${relu.length}×${relu[0].length} → ${pooled.length}×${pooled[0].length}`,'ok');
  };

  const runMultiFilter = () => {
    const img=genImage('checkerboard',14);
    const maps=Object.entries(KERNELS).slice(0,6).map(([name,k])=>({name,map:applyReLU(conv2D(img,k,1,0))}));
    setMfResult({img,maps});
    log(`Multi-filter: ${maps.length} filters applied to same image`,'ok');
  };

  // Architecture param count
  const archParamCount = () => {
    let inChannels=1, inH=32, inW=32, total=0;
    for(const l of archLayers){
      if(l.type==='conv'){const p=l.filters*(inChannels*l.kernel*l.kernel+1);total+=p;inChannels=l.filters;inH=Math.floor((inH+2*l.pad-l.kernel)/l.stride)+1;inW=Math.floor((inW+2*l.pad-l.kernel)/l.stride)+1;}
      else if(l.type==='maxpool'||l.type==='avgpool'){inH=Math.floor((inH-l.size)/l.stride)+1;inW=Math.floor((inW-l.size)/l.stride)+1;}
      else if(l.type==='flatten'){inChannels=inChannels*inH*inW;inH=1;inW=1;}
      else if(l.type==='fc'){total+=inChannels*l.neurons+l.neurons;inChannels=l.neurons;}
    }
    return total;
  };

  const receptiveFields = (() => {
    let rf=1, stride_product=1, pts=[{layer:'Input',rf:1,type:'input'}];
    for(const l of archLayers){
      if(l.type==='conv'){rf+=(l.kernel-1)*stride_product;pts.push({layer:`Conv ${l.kernel}×${l.kernel}`,rf,type:'conv',filters:l.filters});}
      else if(l.type==='maxpool'||l.type==='avgpool'){rf+=l.size*stride_product-stride_product;stride_product*=l.stride;pts.push({layer:`Pool ${l.size}×${l.size}`,rf,type:'pool'});}
    }
    return pts;
  })();

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'#60a5fa':'transparent'}`,background:tab===t?'#60a5fa18':'transparent',color:tab===t?'#60a5fa':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── KERNEL VIZ ── */}
      {tab==='kernel-viz'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="KERNEL CONTROLS" accent="#60a5fa">
              <Sel label="KERNEL" value={kernelName} onChange={k=>{setKernelName(k);setConvResult(null);}} options={Object.keys(KERNELS).map(k=>({value:k,label:k.replace('_',' ').toUpperCase()}))}/>
              <Sel label="IMAGE" value={imgType} onChange={setImgType} options={['checkerboard','gradient','circle','stripes','random'].map(v=>({value:v,label:v.toUpperCase()}))}/>
              <Sl label="Image Size" value={imgSize} min={8} max={20} onChange={setImgSize} color="#60a5fa"/>
              <Sl label="Stride" value={stride} min={1} max={3} onChange={setStride} color="#22d3ee"/>
              <Sl label="Padding" value={padding} min={0} max={2} onChange={setPadding} color="#a78bfa"/>
              <div style={{display:'flex',gap:'6px',marginTop:'4px'}}>
                <Btn onClick={()=>runConv()} color="#60a5fa" style={{flex:1}}>RUN →</Btn>
                <Btn onClick={animateConv} color={animating?'#f87171':'#fbbf24'} style={{flex:1}}>{animating?'STOP':'ANIMATE'}</Btn>
              </div>
            </Panel>
            <Panel title="KERNEL WEIGHTS" accent="#60a5fa">
              <KernelDisplay kernel={KERNELS[kernelName]} label={kernelName.replace('_',' ').toUpperCase()}/>
            </Panel>
            <LogPanel logs={logs} height={90}/>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title={`CONVOLUTION PIPELINE ${animating?'— ANIMATING…':''}`} accent={animating?'#fbbf24':'#60a5fa'}>
              <div style={{display:'flex',gap:'20px',alignItems:'flex-start',flexWrap:'wrap',overflowX:'auto',padding:'8px 0'}}>
                <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                  <PixelGrid matrix={image} title="INPUT IMAGE" color="#60a5fa" cellSize={16} highlight={animating?hlPos:null} kernelSize={KERNELS[kernelName].length}/>
                  <div style={{fontSize:'9px',color:'var(--t3)',textAlign:'center'}}>Yellow = kernel position</div>
                </div>
                <div style={{display:'flex',flexDirection:'column',justifyContent:'center',paddingTop:'30px',fontSize:'24px',color:'var(--t3)'}}>⊛</div>
                <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                  <KernelDisplay kernel={KERNELS[kernelName]} label="KERNEL"/>
                </div>
                <div style={{display:'flex',flexDirection:'column',justifyContent:'center',paddingTop:'30px',fontSize:'20px',color:'var(--t3)'}}>=</div>
                {convResult&&<>
                  <PixelGrid matrix={convResult.raw} title="RAW CONV OUTPUT" color="#f87171" cellSize={18} accent/>
                  <div style={{display:'flex',flexDirection:'column',justifyContent:'center',paddingTop:'30px',fontSize:'16px',color:'var(--t3)'}}>→ReLU→</div>
                  <PixelGrid matrix={convResult.relu} title="AFTER ReLU" color="#4ade80" cellSize={18} accent/>
                </>}
              </div>
            </Panel>

            {convResult&&(
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                <Metric label="Input" value={`${image.length}×${image[0].length}`} color="#60a5fa"/>
                <Metric label="Output" value={`${convResult.raw.length}×${convResult.raw[0].length}`} color="#22d3ee"/>
                <Metric label="Dead neurons" value={`${convResult.relu.flat().filter(v=>v===0).length}/${convResult.relu.flat().length}`} color="#f87171"/>
                <Metric label="Max activation" value={Math.max(...convResult.relu.flat()).toFixed(3)} color="#4ade80"/>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── FEATURE MAPS ── */}
      {tab==='feature-maps'&&(
        <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="CONTROLS" accent="#22d3ee">
              <Sel label="INPUT IMAGE" value="checkerboard" onChange={t=>setFmImage(genImage(t,16))} options={['checkerboard','circle','stripes','gradient'].map(v=>({value:v,label:v.toUpperCase()}))}/>
              <Btn onClick={runFeatureMaps} color="#22d3ee" style={{width:'100%'}}>RUN ALL FILTERS →</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>Same input image passed through every built-in kernel. Compare what each filter detects.</div>
            </Panel>
            <LogPanel logs={logs} height={160}/>
          </div>
          <div>
            <div style={{display:'grid',gridTemplateColumns:'1fr',gap:'8px'}}>
              {!fmResult&&<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Hit RUN ALL FILTERS</div>}
              {fmResult&&(
                <>
                  <Panel title="INPUT IMAGE" accent="#60a5fa">
                    <PixelGrid matrix={fmImage} title="" color="#60a5fa" cellSize={12}/>
                  </Panel>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                    {Object.entries(fmResult).map(([name,{relu}],i)=>(
                      <Panel key={name} title={name.replace('_',' ').toUpperCase()} accent={P[i%P.length]}>
                        <PixelGrid matrix={relu} title="" color={P[i%P.length]} cellSize={12}/>
                        <div style={{fontSize:'9px',color:'var(--t3)',marginTop:'4px'}}>
                          active: {relu.flat().filter(v=>v>0).length}/{relu.flat().length}
                        </div>
                      </Panel>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── POOLING ── */}
      {tab==='pooling'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="POOLING CONTROLS" accent="#a78bfa">
              <Sel label="IMAGE" value="checkerboard" onChange={t=>setPoolImage(genImage(t,12))} options={['checkerboard','circle','stripes','random'].map(v=>({value:v,label:v.toUpperCase()}))}/>
              <Sel label="POOL TYPE" value={poolType} onChange={setPoolType} options={[{value:'max',label:'MAX POOL'},{value:'avg',label:'AVG POOL'}]}/>
              <Sl label="Pool Size" value={poolSize} min={2} max={4} onChange={setPoolSize} color="#a78bfa"/>
              <Btn onClick={runPooling} color="#a78bfa" style={{width:'100%'}}>RUN POOLING →</Btn>
              <div style={{marginTop:'8px',fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>MaxPool keeps the strongest signal in each region. AvgPool smooths. Both reduce spatial dimensions.</div>
            </Panel>
            {poolResult&&(
              <Panel title="DIM REDUCTION" accent="#a78bfa">
                {[['Before pool',`${poolResult.relu.length}×${poolResult.relu[0].length}`,'#22d3ee'],['After pool',`${poolResult.pooled.length}×${poolResult.pooled[0].length}`,'#a78bfa'],['Ratio',`${+(poolResult.relu.flat().length/poolResult.pooled.flat().length).toFixed(1)}×`,'#4ade80']].map(([k,v,c])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:'5px',fontSize:'10px'}}>
                    <span style={{color:'var(--t3)'}}>{k}</span><span style={{color:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</span>
                  </div>
                ))}
              </Panel>
            )}
            <LogPanel logs={logs} height={90}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {poolResult
              ? <Panel title={`POOLING PIPELINE — ${poolType.toUpperCase()}POOL ${poolSize}×${poolSize}`} accent="#a78bfa">
                  <div style={{display:'flex',gap:'20px',alignItems:'flex-start',overflowX:'auto',padding:'8px 0'}}>
                    <PixelGrid matrix={poolImage} title="INPUT" color="#60a5fa" cellSize={18}/>
                    <div style={{paddingTop:30,fontSize:16,color:'var(--t3)'}}>⊛edge</div>
                    <PixelGrid matrix={poolResult.relu} title="FEATURE MAP (after ReLU)" color="#22d3ee" cellSize={18} accent/>
                    <div style={{paddingTop:30,fontSize:16,color:'var(--t3)'}}>→{poolType}pool→</div>
                    <PixelGrid matrix={poolResult.pooled} title={`AFTER ${poolType.toUpperCase()}POOL`} color="#a78bfa" cellSize={22} accent/>
                  </div>
                  <div style={{marginTop:'12px',fontSize:'10px',color:'var(--t3)'}}>Each {poolSize}×{poolSize} region in the feature map → 1 value. Spatial size ÷{poolSize}.</div>
                </Panel>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Configure and run pooling</div>
            }

            {/* Activation distribution before/after */}
            {poolResult&&(
              <Panel title="ACTIVATION DISTRIBUTION — BEFORE vs AFTER POOLING" accent="#fbbf24">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={(()=>{
                    const bef=poolResult.relu.flat(), aft=poolResult.pooled.flat();
                    const mx=Math.max(...bef,...aft)||1, bins=16, bs=mx/bins;
                    const hB=Array(bins).fill(0), hA=Array(bins).fill(0);
                    bef.forEach(v=>{const b=Math.min(bins-1,Math.floor(v/bs));hB[b]++;});
                    aft.forEach(v=>{const b=Math.min(bins-1,Math.floor(v/bs));hA[b]++;});
                    return hB.map((c,i)=>({x:(i*bs).toFixed(1),before:c,after:hA[i]}));
                  })()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="x" tick={{fontSize:8,fill:'var(--t3)'}}/>
                    <YAxis tick={{fontSize:8,fill:'var(--t3)'}} width={20}/>
                    <Tooltip/>
                    <Legend iconSize={8} wrapperStyle={{fontSize:9}}/>
                    <Bar dataKey="before" fill="#22d3ee" opacity={0.7} name="Before pool" radius={[1,1,0,0]}/>
                    <Bar dataKey="after" fill="#a78bfa" opacity={0.85} name="After pool" radius={[1,1,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
            )}
          </div>
        </div>
      )}

      {/* ── MULTI-FILTER ── */}
      {tab==='multi-filter'&&(
        <div style={{display:'grid',gridTemplateColumns:'180px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="CONTROLS" accent="#fbbf24">
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',lineHeight:1.5}}>Real CNNs learn dozens of filters simultaneously. Each detects a different feature.</div>
              <Btn onClick={runMultiFilter} color="#fbbf24" style={{width:'100%'}}>RUN MULTI-FILTER →</Btn>
            </Panel>
            <LogPanel logs={logs} height={200}/>
          </div>
          <div>
            {mfResult
              ? <>
                  <Panel title="INPUT" accent="#60a5fa" style={{marginBottom:10}}>
                    <PixelGrid matrix={mfResult.img} title="SHARED INPUT IMAGE" color="#60a5fa" cellSize={14}/>
                  </Panel>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
                    {mfResult.maps.map(({name,map},i)=>(
                      <Panel key={name} title={name.replace(/_/g,' ').toUpperCase()} accent={P[i%P.length]}>
                        <PixelGrid matrix={map} title="" color={P[i%P.length]} cellSize={14}/>
                        <div style={{marginTop:'6px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px'}}>
                          {[['max',Math.max(...map.flat()).toFixed(2)],['active%',+(map.flat().filter(v=>v>0).length/map.flat().length*100).toFixed(0)+'%']].map(([k,v])=>(
                            <div key={k} style={{background:'var(--deep)',padding:'4px 6px',borderRadius:'2px'}}>
                              <div style={{fontSize:'8px',color:'var(--t3)'}}>{k}</div>
                              <div style={{fontSize:'10px',color:P[i%P.length],fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</div>
                            </div>
                          ))}
                        </div>
                      </Panel>
                    ))}
                  </div>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Hit RUN MULTI-FILTER to see how different filters see the same image</div>
            }
          </div>
        </div>
      )}

      {/* ── ARCHITECTURE ── */}
      {tab==='architecture'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          <Panel title="CNN ARCHITECTURE BUILDER — 32×32 RGB INPUT" accent="#4ade80">
            <div style={{display:'flex',flexDirection:'column',gap:'4px',maxHeight:400,overflowY:'auto'}}>
              {archLayers.map((l,i)=>{
                const color=l.type==='conv'?'#60a5fa':l.type==='relu'?'#4ade80':l.type==='maxpool'||l.type==='avgpool'?'#a78bfa':l.type==='flatten'?'#fbbf24':l.type==='fc'?'#fb923c':'#64748b';
                return (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:'8px',padding:'8px 12px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${color}`}}>
                    <span style={{fontSize:'10px',color,fontFamily:'JetBrains Mono',fontWeight:700,minWidth:80}}>{l.type.toUpperCase()}</span>
                    <span style={{fontSize:'10px',color:'var(--t2)',flex:1}}>
                      {l.type==='conv'&&`${l.filters} filters · ${l.kernel}×${l.kernel} · s=${l.stride} p=${l.pad}`}
                      {l.type==='maxpool'&&`${l.size}×${l.size} stride=${l.stride}`}
                      {l.type==='fc'&&`${l.neurons} neurons`}
                      {l.type==='relu'&&'ReLU activation'}
                      {l.type==='flatten'&&'Flatten to 1D'}
                    </span>
                  </div>
                );
              })}
            </div>
            <div style={{marginTop:'12px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
              <Metric label="Total Params" value={archParamCount().toLocaleString()} color="#4ade80"/>
              <Metric label="Layers" value={archLayers.length} color="#60a5fa"/>
            </div>
          </Panel>

          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="RECEPTIVE FIELD GROWTH" accent="#fbbf24">
              <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                {receptiveFields.map((pt,i)=>(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:'10px'}}>
                    <span style={{fontSize:'9px',color:'var(--t3)',minWidth:120}}>{pt.layer}</span>
                    <div style={{flex:1,height:16,background:'var(--deep)',borderRadius:'2px',overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${Math.min(100,pt.rf/20*100)}%`,background:pt.type==='conv'?'#60a5fa':pt.type==='pool'?'#a78bfa':'#4ade80',borderRadius:'2px',transition:'width .3s',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'8px',color:'#000',fontWeight:700}}>
                        {pt.rf}px
                      </div>
                    </div>
                    <span style={{fontSize:'9px',color:'#fbbf24',minWidth:30,textAlign:'right'}}>{pt.rf}px RF</span>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="CLASSIC ARCHITECTURES" accent="#60a5fa">
              {[
                {name:'LeNet-5',year:1998,params:'60K',layers:'Conv→Pool→Conv→Pool→FC→FC',color:'#60a5fa'},
                {name:'AlexNet',year:2012,params:'60M',layers:'5×Conv + 3×FC + Dropout',color:'#22d3ee'},
                {name:'VGG-16',year:2014,params:'138M',layers:'13×Conv(3×3) + 3×FC',color:'#4ade80'},
                {name:'ResNet-50',year:2015,params:'25M',layers:'Residual blocks (skip connections)',color:'#a78bfa'},
                {name:'EfficientNet',year:2019,params:'5.3M',layers:'Compound scaling (d×w×r)',color:'#fbbf24'},
              ].map(a=>(
                <div key={a.name} style={{display:'flex',gap:'10px',padding:'8px',background:'var(--deep)',borderRadius:'3px',marginBottom:'5px',borderLeft:`2px solid ${a.color}`}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:'11px',color:a.color,fontWeight:700}}>{a.name} <span style={{fontSize:'9px',color:'var(--t3)',fontWeight:400}}>({a.year})</span></div>
                    <div style={{fontSize:'9px',color:'var(--t2)',marginTop:'2px'}}>{a.layers}</div>
                  </div>
                  <div style={{padding:'2px 8px',background:a.color+'18',border:`1px solid ${a.color}44`,borderRadius:'2px',fontSize:'9px',color:a.color,alignSelf:'center',whiteSpace:'nowrap'}}>{a.params}</div>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
